import React, { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { toast } from "react-toastify";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Dropdown from "src/components/ui/Dropdown";
import Skeleton from "src/components/ui/Skeleton";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import GatewaySettingsCard from "src/components/payments/GatewaySettingsCard";
import { formatExpiryMinutes } from "src/components/payments/paymentLabels";
import paymentApis from "src/api/payments";
import { useAppSelector } from "src/redux/hooks";
import { userCan } from "src/utils/permissions";
import { PaymentSettingsResponse } from "src/types/salePayment.types";

const SettingsPaymentsPage: React.FC = () => {
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const canSetup = userCan(user, "payments", "setup");

  const [settings, setSettings] = useState<PaymentSettingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingProvider, setSavingProvider] = useState("");
  const [disconnectTarget, setDisconnectTarget] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);
  const [expiry, setExpiry] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);

  const applySettings = (data: PaymentSettingsResponse) => {
    setSettings(data);
    setExpiry(String(data.linkExpiryMinutes));
  };

  useEffect(() => {
    if (!canSetup) return;
    setLoading(true);
    paymentApis
      .getSettings()
      .then((res: any) => applySettings(res.data))
      .catch((error: any) => toast.error(error.message || "Failed to load payment settings"))
      .finally(() => setLoading(false));
  }, [canSetup]);

  const handleSaveGateway = async (provider: string, values: Record<string, string>) => {
    try {
      setSavingProvider(provider);
      const res: any = await paymentApis.saveGateway(provider, values);
      applySettings(res.data);
      toast.success(res.message || "Saved");
      return true;
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
      return false;
    } finally {
      setSavingProvider("");
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      const res: any = await paymentApis.disconnectGateway(disconnectTarget);
      applySettings(res.data);
      toast.success(res.message || "Disconnected");
      setDisconnectTarget("");
    } catch (error: any) {
      toast.error(error.message || "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveExpiry = async () => {
    try {
      setSavingExpiry(true);
      const res: any = await paymentApis.savePreferences({
        linkExpiryMinutes: Number(expiry),
      });
      applySettings(res.data);
      toast.success("Link expiry saved");
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    } finally {
      setSavingExpiry(false);
    }
  };

  const disconnectName =
    settings?.providers.find((p) => p.key === disconnectTarget)?.displayName || "";

  return (
    <DashboardLayout
      meta={{
        title: "Payments | Invyntra",
        description: "Connect a payment gateway to collect sale order payments online.",
        keywords: "payments, razorpay, payment gateway, settings, invyntra",
      }}
    >
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <Wallet size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Payments</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Connect your own payment gateway account — customers pay straight into it.
            </p>
          </div>
        </div>

        {!canSetup ? (
          <Card>
            <div className="p-6 text-center text-sm text-gray-600 dark:text-gray-300">
              You don't have permission to set up payments.
            </div>
          </Card>
        ) : loading || !settings ? (
          <Card>
            <div className="space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </Card>
        ) : (
          <>
            {settings.providers.map((gateway) => (
              <GatewaySettingsCard
                key={gateway.key}
                gateway={gateway}
                saving={savingProvider === gateway.key}
                disconnecting={disconnecting && disconnectTarget === gateway.key}
                onSave={(values) => handleSaveGateway(gateway.key, values)}
                onDisconnect={() => setDisconnectTarget(gateway.key)}
              />
            ))}

            <Card>
              <CardHeader
                title="Payment links"
                description="Applies to links generated from now on."
              />
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-full sm:w-64">
                  <Dropdown
                    label="Link expires after"
                    fullWidth
                    value={expiry}
                    options={settings.linkExpiryOptions.map((m) => ({
                      value: String(m),
                      label: formatExpiryMinutes(m),
                    }))}
                    onChange={setExpiry}
                  />
                </div>
                <Button
                  onClick={handleSaveExpiry}
                  isLoading={savingExpiry}
                  disabled={
                    !expiry ||
                    expiry === String(settings.linkExpiryMinutes) ||
                    savingExpiry
                  }
                >
                  Save
                </Button>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                An expired link keeps its order as it is (stock stays reserved) — staff can
                regenerate a link or record the payment offline.
              </p>
            </Card>
          </>
        )}
      </div>

      {!!disconnectTarget && (
        <ConfirmationModal
          open={!!disconnectTarget}
          title={`Disconnect ${disconnectName}?`}
          message="Saved keys will be deleted. Staff won't be able to create payment links until a gateway is connected again. Links already sent stop updating here."
          variant="danger"
          confirmLabel="Disconnect"
          onConfirm={handleDisconnect}
          onCancel={() => setDisconnectTarget("")}
          isLoading={disconnecting}
        />
      )}
    </DashboardLayout>
  );
};

export default SettingsPaymentsPage;
